<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiAvatar, UiIcon, UiNavLink, generateInitials } from "@shopexperts/shared";
import type { ExpertInboxProject } from "@/v2Projects/types";
import QuoteDetailStrip from "./QuoteDetailStrip.vue";
import ProjectOpenLinkLabel from "./ProjectOpenLinkLabel.vue";
import {
  getStatusConfig,
  formatRelativeTime,
  formatShortDate,
} from "@/v2Projects/utils/display";

const props = defineProps<{
  project: ExpertInboxProject;
}>();

const status = computed(() => getStatusConfig(props.project.project_status, props.project.project_type));
const projectRoute = computed(() => ({
  name: "expert-project-details",
  params: { id: String(props.project.id) },
}));
const matchTypeBadge = computed(() => {
  if (props.project.project_type === "booking_call") return null;

  if (props.project.match_type === "client_selected") {
    return {
      label: "Direct",
      class: "bg-blue-100 text-blue-800",
    };
  }

  if (props.project.match_type === "admin_matched") {
    return {
      label: "Matched",
      class: "bg-orange-100 text-orange-800",
    };
  }

  return null;
});

const client = computed(() => props.project.client);
const clientName = computed(() =>
  client.value?.owner_name || client.value?.name || "Unknown Client",
);
const clientInitials = computed(() => generateInitials(clientName.value));
const clientOrgName = computed(() => client.value?.organization?.name ?? null);

const createdDate = computed(() => formatShortDate(props.project.created_at));

const activeQuote = computed(() => props.project.latest_active_quote ?? null);

const lastMessage = computed(() => {
  const lm = props.project.last_message;
  if (!lm) return null;
  const senderName = lm.user
    ? [lm.user.first_name, lm.user.last_name].filter(Boolean).join(" ") || lm.user.email
    : lm.org?.name;
  return {
    text: lm.excerpt ?? "",
    sender: senderName ?? "Someone",
    time: formatRelativeTime(lm.sent_at),
  };
});
</script>

<template>
  <UiNavLink
    :to="projectRoute"
    class="block bg-white border border-gray-200 rounded-xl transition-all duration-200 hover:border-gray-300 hover:shadow-[0_2px_8px_rgb(0_0_0/0.04)] cursor-pointer group/ui-button no-underline text-inherit"
  >
    <!-- Top bar: status + date -->
    <div class="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
      <div class="flex items-center gap-2 flex-wrap">
        <span
          :class="status.badgeClass"
          class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
        >
          {{ status.label }}
        </span>
        <span
          v-if="matchTypeBadge"
          :class="matchTypeBadge.class"
          class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
        >
          {{ matchTypeBadge.label }}
        </span>
      </div>
      <UiText v-if="createdDate" as="span" variant="p-small" class="text-gray-600 shrink-0">
        {{ createdDate }}
      </UiText>
    </div>

    <!-- Main row -->
    <div class="flex items-start gap-3 px-5 py-3">
      <!-- Avatar with status ring -->
      <div
        class="shrink-0 rounded-full bg-white p-[2px] ring-2 mt-0.5"
        :class="status.avatarRing"
      >
        <UiAvatar
          :initials="clientInitials"
          :name="clientName"
          size="xs"
          :responsive="false"
          color-coded
        />
      </div>

      <!-- Content -->
      <div class="min-w-0 flex-1 lg:flex-initial lg:min-w-64">
        <!-- Project title -->
        <UiText as="h4" variant="p" class="font-semibold text-gray-900 truncate leading-snug">
          {{ project.title }}
        </UiText>

        <!-- Client name + org -->
        <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <UiText as="span" variant="p-small" class="text-gray-600 truncate">
            {{ clientName }}
          </UiText>
          <template v-if="clientOrgName && clientOrgName !== clientName">
            <span class="text-gray-400">&middot;</span>
            <UiText as="span" variant="p-small" class="text-gray-600 truncate">
              {{ clientOrgName }}
            </UiText>
          </template>
        </div>

        <!-- Last message -->
        <div v-if="lastMessage" class="flex items-center gap-1.5 mt-1.5">
          <UiIcon name="chat-bubble" class="w-3 h-3 text-gray-500 shrink-0" />
          <UiText as="span" variant="p-small" class="text-gray-600 truncate min-w-0">
            <span class="font-medium text-gray-800">{{ lastMessage.sender }}:</span>
            {{ lastMessage.text }}
          </UiText>
          <UiText as="span" variant="p-small" class="text-gray-500 shrink-0">
            &middot; {{ lastMessage.time }}
          </UiText>
        </div>
        <UiText v-else-if="project.description" as="p" variant="p-small" class="mt-1.5 line-clamp-1 text-gray-600 min-w-0">
          {{ project.description }}
        </UiText>
      </div>

      <!-- Right cluster: quote + action -->
      <div class="hidden md:flex ml-auto items-center self-center shrink-0">
        <div v-if="activeQuote" class="hidden lg:flex shrink-0 mr-8 xl:mr-12">
          <QuoteDetailStrip :quote="activeQuote" />
        </div>

        <div class="shrink-0">
          <ProjectOpenLinkLabel />
        </div>
      </div>
    </div>
  </UiNavLink>
</template>
